/**
 * ActionsMenu · DEC-052 Workflow Actions.
 *
 * The application design language now says:
 * every operational artifact carries an Actions menu whose actions
 * create work with provenance back to the originating artifact.
 *
 * Usage:
 *   const actions = [
 *     {
 *       key: "create-fuel-order",
 *       label: "Create Fuel Order",
 *       icon: Truck,
 *       prefill: {
 *         task_type: "Dispatch",
 *         subject: `Fuel order · ${customerName}`,
 *         description: message,
 *         customer_account, customer_name,
 *         source_type: "phone_message",
 *         source_id: phoneMessageId,
 *         source_ref: { section: "root" },
 *       },
 *     },
 *   ];
 *   <ActionsMenu actions={actions} onCreated={onCreated}
 *                dataTestidPrefix="pm-actions" />
 */
import { useEffect, useRef, useState } from "react";
import { DotsThreeVertical } from "@phosphor-icons/react";
import NewTaskDialog from "./NewTaskDialog";

export default function ActionsMenu({
  actions = [],
  onCreated,
  buttonLabel = null,        // when set, renders a labelled button instead of the dots
  dataTestidPrefix = "actions",
  align = "right",           // menu alignment · "right" (default) or "left"
  size = "sm",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const trigger = (action) => {
    setPrefill(action.prefill);
    setOpen(false);
  };

  const closeDialog = () => setPrefill(null);
  const doneCreating = () => {
    setPrefill(null);
    onCreated?.();
  };

  return (
    <>
      <div className="relative inline-block" ref={ref}>
        <button
          data-testid={`${dataTestidPrefix}-trigger`}
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          className={`inline-flex items-center gap-1 rounded border border-slate-200 bg-white ${size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"} text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {buttonLabel ? (
            <span className="font-medium">{buttonLabel}</span>
          ) : null}
          <DotsThreeVertical size={14} weight="bold" />
        </button>
        {open && (
          <div
            data-testid={`${dataTestidPrefix}-menu`}
            className={`absolute z-40 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg ${align === "right" ? "right-0" : "left-0"}`}
          >
            <ul className="py-1">
              {actions.map((a) => (
                <li key={a.key}>
                  <button
                    onClick={() => trigger(a)}
                    data-testid={`${dataTestidPrefix}-item-${a.key}`}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 text-left"
                  >
                    {a.icon ? <a.icon size={13} className="text-slate-500" /> : null}
                    {a.label}
                  </button>
                </li>
              ))}
              {actions.length === 0 && (
                <li className="px-3 py-2 text-xs text-slate-500">No actions available.</li>
              )}
            </ul>
          </div>
        )}
      </div>
      {prefill && (
        <NewTaskDialog
          prefill={prefill}
          onClose={closeDialog}
          onCreated={doneCreating}
        />
      )}
    </>
  );
}
